import { initSentry, captureException, addBreadcrumb } from "@/lib/sentry";
import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";

import "./index.css";

// Initialize Sentry for error monitoring (must be first)
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // dados de BI mudam devagar — evita refetch a cada navegação/foco
      staleTime: 5 * 60 * 1000, // 5 min "fresco"
      gcTime: 30 * 60 * 1000, // mantém em cache 30 min
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Don't redirect if already on login/cadastro pages
  if (window.location.pathname === "/login" || window.location.pathname === "/cadastro") return;
  window.location.href = "/login";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);

    // Capture non-auth errors in Sentry
    if (error instanceof TRPCClientError && error.message !== UNAUTHED_ERR_MSG) {
      addBreadcrumb("trpc.query", `Query error: ${error.message}`, {
        queryKey: String(event.query.queryKey),
      }, "error");
      captureException(error, {
        source: "trpc.query",
        queryKey: String(event.query.queryKey),
      });
    }

    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);

    // Capture non-auth errors in Sentry
    if (error instanceof TRPCClientError && error.message !== UNAUTHED_ERR_MSG) {
      addBreadcrumb("trpc.mutation", `Mutation error: ${error.message}`, {}, "error");
      captureException(error, {
        source: "trpc.mutation",
      });
    }

    console.error("[API Mutation Error]", error);
  }
});

/**
 * Fetch wrapper that automatically refreshes the session on 401.
 * When a tRPC batch call returns UNAUTHORIZED, we call /api/auth/refresh
 * once, then retry the original request. This prevents the user from
 * being logged out every time the Supabase JWT (1h) expires.
 */
let refreshPromise: Promise<boolean> | null = null;

async function fetchWithRefresh(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await globalThis.fetch(input, { ...(init ?? {}), credentials: "include" });

  // If not 401, return as-is
  if (res.status !== 401) return res;

  // Try to detect UNAUTHORIZED in the tRPC JSON response body
  const cloned = res.clone();
  let isAuthError = false;
  try {
    const body = await cloned.json();
    // tRPC batch responses are arrays
    const items = Array.isArray(body) ? body : [body];
    isAuthError = items.some(
      (item: any) =>
        item?.error?.message === UNAUTHED_ERR_MSG ||
        item?.error?.data?.code === "UNAUTHORIZED"
    );
  } catch {
    // Not JSON or parse error — treat as auth error if 401
    isAuthError = true;
  }

  if (!isAuthError) return res;

  // Deduplicate concurrent refresh calls
  if (!refreshPromise) {
    refreshPromise = globalThis
      .fetch("/api/auth/refresh", { method: "POST", credentials: "include" })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }

  const refreshed = await refreshPromise;
  if (!refreshed) return res; // refresh failed, return original 401

  // Retry the original request with the new cookies
  return globalThis.fetch(input, { ...(init ?? {}), credentials: "include" });
}

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch: fetchWithRefresh,
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
